/* eslint-disable max-lines-per-function */
import { api } from "jiffy-api";
import trackEtaEstimates from "common/utils/delivery-date/track-eta-estimates";
import businessLineToUrlGroup from "common/utils/delivery-date/business-line-group";

const DEFAULT_BATCH_DELAY_MS = 50;

export default class CompositionEstimatesClass {
  #batches = new Map();

  #ongoingRequests = new Map(); // Track ongoing requests to prevent duplicates

  #batchDelayMs;

  constructor({ batchDelayMs = DEFAULT_BATCH_DELAY_MS } = {}) {
    this.#batchDelayMs = batchDelayMs;
  }

  /**
   * Fetches composition estimates with immediate execution for individual requests
   * @param {Array} compositionIds - Array of composition IDs to fetch
   * @param {Object} options - Options object
   * @param {boolean} options.isMade - Whether to fetch made compositions
   * @returns {Promise<[Object, number]>} Promise resolving to [data, duration]
   */
  async fetch(compositionIds, { isMade = false, isSendIndividually = false, businessLine } = {}) {
    const validIds = compositionIds.filter(Boolean);

    if (!validIds.length) {
      return [{}, 0];
    }

    // Use individual requests only when isSendIndividually is true
    if (isSendIndividually) {
      // For individual requests, check for ongoing requests and execute immediately without batching
      if (validIds.length === 1) {
        const compositionId = validIds[0];
        const requestKey = `${compositionId}-${isMade}-${businessLine}`;

        // Check if there's already an ongoing request for this composition ID
        if (this.#ongoingRequests.has(requestKey)) {
          return this.#ongoingRequests.get(requestKey);
        }

        const startTime = performance.now();
        const urlGroup = businessLineToUrlGroup(businessLine);
        const useFastEta =
          CompositionEstimatesClass.shouldUseFastEta(businessLine) && urlGroup !== null;
        const apiMethod = useFastEta ? api.product.fetchETA : api.product.fetchCompositionEstimates;

        const params = useFastEta
          ? { composition_hash: { [compositionId]: 1 } }
          : {
              composition_ids: compositionId,
              ...(isMade && { made: "true" })
            };

        const requestPromise = (async () => {
          try {
            const data = useFastEta
              ? await apiMethod({ business_line: urlGroup }, params)
              : await apiMethod(params);
            const endTime = performance.now();
            const duration = Math.round(endTime - startTime);
            if (useFastEta) trackEtaEstimates(data, { cap: true });
            return [data, duration];
          } finally {
            // Clean up the ongoing request when it completes
            this.#ongoingRequests.delete(requestKey);
          }
        })();

        // Store the promise to prevent duplicate requests
        this.#ongoingRequests.set(requestKey, requestPromise);
        return requestPromise;
      }

      const urlGroup = businessLineToUrlGroup(businessLine);
      const useFastEta =
        CompositionEstimatesClass.shouldUseFastEta(businessLine) && urlGroup !== null;

      const promises = validIds.map(compositionId => {
        const requestKey = `${compositionId}-${isMade}-${businessLine}`;

        if (this.#ongoingRequests.has(requestKey)) {
          return this.#ongoingRequests.get(requestKey);
        }

        const startTime = performance.now();
        const apiMethod = useFastEta ? api.product.fetchETA : api.product.fetchCompositionEstimates;

        const params = useFastEta
          ? { composition_hash: { [compositionId]: 1 } }
          : {
              composition_ids: compositionId,
              ...(isMade && { made: "true" })
            };

        const requestPromise = (async () => {
          try {
            const data = useFastEta
              ? await apiMethod({ business_line: urlGroup }, params)
              : await apiMethod(params);
            const endTime = performance.now();
            const duration = Math.round(endTime - startTime);
            if (useFastEta) trackEtaEstimates(data, { cap: true });
            return [data, duration];
          } finally {
            this.#ongoingRequests.delete(requestKey);
          }
        })();

        this.#ongoingRequests.set(requestKey, requestPromise);
        return requestPromise;
      });

      // Return the first result for backward compatibility
      const firstResult = await promises[0];
      return firstResult;
    }

    // For non-made requests, use the original batching logic regardless of number of IDs
    const batchKey = `${isMade}-${businessLine}`;
    const startTime = performance.now();

    return new Promise((resolve, reject) => {
      const batch = this.#getOrCreateBatch(batchKey);

      validIds.forEach(id => batch.ids.add(id));
      batch.callbacks.push({ resolve, reject, startTime });

      this.#scheduleBatchExecution(batch, batchKey, { isMade, businessLine });
    });
  }

  static shouldUseFastEta(businessLine) {
    if (!window.jiffy?.flip?.fastDeliveryEstimate) return false;
    return businessLineToUrlGroup(businessLine) !== null;
  }

  #getOrCreateBatch(key) {
    if (!this.#batches.has(key)) {
      this.#batches.set(key, {
        ids: new Set(),
        callbacks: [],
        timer: null
      });
    }
    return this.#batches.get(key);
  }

  #scheduleBatchExecution(batch, batchKey, batchOptions) {
    clearTimeout(batch.timer);
    // eslint-disable-next-line no-param-reassign -- batch is a mutable cache entry
    batch.timer = setTimeout(() => this.#executeBatch(batchKey, batchOptions), this.#batchDelayMs);
  }

  async #executeBatch(batchKey, { isMade, businessLine }) {
    const batch = this.#batches.get(batchKey);
    if (!batch) return;

    const { ids, callbacks } = batch;
    this.#batches.delete(batchKey);

    const urlGroup = businessLineToUrlGroup(businessLine);
    const useFastEta =
      CompositionEstimatesClass.shouldUseFastEta(businessLine) && urlGroup !== null;
    const apiMethod = useFastEta ? api.product.fetchETA : api.product.fetchCompositionEstimates;

    const params = useFastEta
      ? { composition_hash: Object.fromEntries(Array.from(ids).map(id => [id, 1])) }
      : {
          composition_ids: Array.from(ids).join(","),
          ...(isMade && { made: "true" })
        };

    try {
      const data = useFastEta
        ? await apiMethod({ business_line: urlGroup }, params)
        : await apiMethod(params);
      const endTime = performance.now();

      if (useFastEta) trackEtaEstimates(data, { cap: true });

      callbacks.forEach(({ resolve, startTime }) => {
        const duration = Math.round(endTime - startTime);
        resolve([data, duration]);
      });
    } catch (error) {
      callbacks.forEach(({ reject }) => reject(error));
    }
  }
}
